/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';

import {
    Checkbox,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    MenuProps,
    Select,
} from '@mui/material';
import { Column } from '@material-table/core';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const menuProps: Partial<MenuProps> = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
    variant: 'menu',
};

function getLocalizedFilterPlaceHolder<T extends object>(columnDef: Column<T>) {
    return columnDef.filterPlaceholder || '';
}

interface LookupFilterProps<T extends object> {
    columnDef: Column<T> & {
        tableData?: { id: number; filterValue: any; lookup: Record<string, any> };
    };
    // The columnId can be extracted from columnDef.tableData.id
    onFilterChanged: (columnId: number, value: any) => void;
    forwardedRef: React.Ref<any>;
}

function LookupFilter<T extends object>({
    columnDef,
    onFilterChanged,
    forwardedRef,
}: LookupFilterProps<T>) {
    const [selectedFilter, setSelectedFilter] = useState(columnDef.tableData?.filterValue || []);

    useEffect(() => {
        setSelectedFilter(columnDef.tableData?.filterValue || []);
    }, [columnDef.tableData?.filterValue]);

    return (
        <FormControl style={{ width: '100%' }} ref={forwardedRef}>
            <InputLabel
                htmlFor={'select-multiple-checkbox' + columnDef.tableData?.id}
                style={{ marginTop: -16 }}
            >
                {getLocalizedFilterPlaceHolder(columnDef)}
            </InputLabel>
            <Select
                multiple
                value={selectedFilter}
                onClose={() => {
                    if (columnDef.filterOnItemSelect !== true) {
                        onFilterChanged(columnDef.tableData?.id || 0, selectedFilter);
                    }
                }}
                size="small"
                onChange={(event) => {
                    setSelectedFilter(event.target.value);
                    if (columnDef.filterOnItemSelect === true) {
                        onFilterChanged(columnDef.tableData?.id || 0, event.target.value);
                    }
                }}
                labelId={'select-multiple-checkbox' + columnDef.tableData?.id}
                renderValue={(selectedArr) =>
                    selectedArr
                        // @ts-expect-error from https://github.com/material-table-core/core/blob/master/src/components/MTableFilterRow/LookupFilter.js
                        .map((selected: string) => columnDef.lookup[selected] || '')
                        .join(', ')
                }
                MenuProps={menuProps}
                style={{ marginTop: 0 }}
                slotProps={{ input: { sx: { paddingY: 0.5 } } }}
            >
                {/* @ts-expect-error from https://github.com/material-table-core/core/blob/master/src/components/MTableFilterRow/LookupFilter.js */}
                {Object.keys(columnDef.lookup).map((key) => (
                    <MenuItem key={key} value={key}>
                        <Checkbox checked={selectedFilter.indexOf(key.toString()) > -1} />
                        {/* @ts-expect-error from https://github.com/material-table-core/core/blob/master/src/components/MTableFilterRow/LookupFilter.js */}
                        <ListItemText primary={columnDef.lookup[key]} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

type PassedProps<T extends object> = Omit<LookupFilterProps<T>, 'forwardedRef'>;

export default React.forwardRef<unknown, PassedProps<any>>(function LookupFilterRef(
    props: PassedProps<any>,
    ref,
) {
    return <LookupFilter {...props} forwardedRef={ref} />;
});
